import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';

// ─────────────────────────────────────────────────────────────────────────
// TipsContext — gère les bulles d'astuces (onboarding) côté frontend.
// L'état est persisté côté serveur (`/api/auth/me` expose `tips_enabled` +
// `dismissed_tips`) pour suivre l'utilisateur sur tous ses appareils.
// ─────────────────────────────────────────────────────────────────────────

const TipsContext = createContext();

export const useTips = () => {
  const ctx = useContext(TipsContext);
  if (!ctx) throw new Error('useTips must be used within a TipsProvider');
  return ctx;
};

export const TipsProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [tipsEnabled, setTipsEnabled] = useState(true);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);

  // Charge l'état depuis /auth/me au login.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setTipsEnabled(true);
      setDismissedIds(new Set());
      setLoaded(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setTipsEnabled(res.data?.tips_enabled !== false);
        setDismissedIds(new Set(res.data?.dismissed_tips || []));
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Sauve côté serveur (best effort).
  const persist = useCallback(async (payload) => {
    if (!token) return;
    try {
      await axios.put(`${API}/auth/tips`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* silencieux */ }
  }, [token]);

  const dismissTip = useCallback((tipId) => {
    setDismissedIds(prev => {
      if (prev.has(tipId)) return prev;
      const next = new Set(prev);
      next.add(tipId);
      persist({ dismissed_tips: Array.from(next) });
      return next;
    });
  }, [persist]);

  const isDismissed = useCallback((tipId) => dismissedIds.has(tipId), [dismissedIds]);

  // Affiche une astuce si : globalement activées + pas encore fermée + état chargé.
  const shouldShow = useCallback((tipId) => loaded && tipsEnabled && !dismissedIds.has(tipId), [loaded, tipsEnabled, dismissedIds]);

  const toggleTipsEnabled = useCallback((enabled) => {
    setTipsEnabled(enabled);
    if (enabled) {
      // Réactivation : on remet à zéro tous les tips dismissés pour que
      // les bulles d'astuce et avertissements (disclaimer Marché, etc.)
      // réapparaissent comme à la première utilisation.
      setDismissedIds(new Set());
      persist({ tips_enabled: true, dismissed_tips: [] });
    } else {
      persist({ tips_enabled: false });
    }
  }, [persist]);

  const resetAllTips = useCallback(() => {
    setDismissedIds(new Set());
    persist({ dismissed_tips: [] });
  }, [persist]);

  const value = useMemo(() => ({
    tipsEnabled,
    dismissedIds,
    isDismissed,
    shouldShow,
    dismissTip,
    toggleTipsEnabled,
    resetAllTips,
  }), [tipsEnabled, dismissedIds, isDismissed, shouldShow, dismissTip, toggleTipsEnabled, resetAllTips]);

  return <TipsContext.Provider value={value}>{children}</TipsContext.Provider>;
};

export default TipsContext;
